import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Trash2,
  AlertCircle,
  Plus,
  Minus,
  Target,
  Zap,
  Shield,
  Activity,
  Home,
  Settings,
} from 'lucide-react';

const Portfolio: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<
    'overview' | 'positions' | 'orders' | 'history'
  >('overview');

  const navigationMenu = [
    { name: 'Dashboard', href: '/miai', icon: Home, current: false },
    { name: 'Portfolio', href: '/portfolio', icon: TrendingUp, current: true },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, current: false },
    { name: 'Settings', href: '/settings', icon: Settings, current: false },
  ];

  // Enhanced mock portfolio data
  const portfolioStats = {
    balance: 45678.9,
    equity: 125000.0,
    margin: 23456.78,
    freeMargin: 22222.12,
    marginLevel: 533.45,
    unrealizedPnL: 3125.0,
    realizedPnL: 5678.9,
    totalReturn: 25.6,
    dailyPnL: 234.5,
    weeklyPnL: 1234.7,
  };

  const openPositions = [
    {
      id: 1,
      symbol: 'BTC/USD',
      type: 'buy',
      size: 1.10,
      openPrice: 44200.0,
      currentPrice: 45234.56,
      unrealizedPnL: 517.28,
      margin: 11050.0,
      leverage: '4x',
      openTime: '2024-01-15 10:30:00',
      stopLoss: 43000.0,
      takeProfit: 47000.0,
      swap: -2.34,
    },
    {
      id: 2,
      symbol: 'ETH/USD',
      type: 'sell',
      size: 2.0,
      openPrice: 3250.0,
      currentPrice: 3234.56,
      unrealizedPnL: -30.88,
      margin: 3250.0,
      leverage: '2x',
      openTime: '2024-01-14 15:45:00',
      stopLoss: 3350.0,
      takeProfit: 3150.0,
      swap: -1.23,
    },
    {
      id: 3,
      symbol: 'EURUSD',
      type: 'buy',
      size: 10000,
      openPrice: 1.085,
      currentPrice: 1.0892,
      unrealizedPnL: 42.0,
      margin: 1085.0,
      leverage: '100x',
      openTime: '2024-01-13 09:15:00',
      stopLoss: 1.080,
      takeProfit: 1.095,
      swap: -0.45,
    },
  ];

  const pendingOrders = [
    {
      id: 1,
      symbol: 'BTC/USD',
      type: 'buy',
      orderType: 'limit',
      size: 0.3,
      price: 44000.0,
      time: '2024-01-15 14:20:00',
      status: 'pending',
      stopLoss: 43000.0,
      takeProfit: 47000.0,
    },
    {
      id: 2,
      symbol: 'GOLD',
      type: 'sell',
      orderType: 'stop',
      size: 1.0,
      price: 2050.0,
      time: '2024-01-15 11:30:00',
      status: 'pending',
      stopLoss: 2100.0,
      takeProfit: 2000.0,
    },
  ];

  const closedPositions = [
    {
      id: 1,
      symbol: 'AAPL',
      type: 'buy',
      size: 10,
      openPrice: 175.0,
      closePrice: 178.45,
      realizedPnL: 34.5,
      openTime: '2024-01-10 10:00:00',
      closeTime: '2024-01-12 14:30:00',
      duration: '2 days',
      swap: -0.12,
    },
    {
      id: 2,
      symbol: 'OIL',
      type: 'sell',
      size: 5,
      openPrice: 80.0,
      closePrice: 78.45,
      realizedPnL: 7.75,
      openTime: '2024-01-08 09:00:00',
      closeTime: '2024-01-11 16:00:00',
      duration: '3 days',
      swap: -0.08,
    },
  ];

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'positions', name: 'Open Positions', icon: TrendingUp },
    { id: 'orders', name: 'Pending Orders', icon: Clock },
    { id: 'history', name: 'Closed Positions', icon: CheckCircle },
  ];

  const closePosition = (id: number) => {
    // Close position logic here
  };

  const cancelOrder = (id: number) => {
    // Cancel order logic here
  };

  return (
    <div className="h-screen overflow-hidden flex flex-col">
      {/* Navigation Menu */}
      <div className="card p-4 flex-shrink-0">
        <nav className="flex space-x-8">
          {navigationMenu.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.name}
                onClick={() => navigate(item.href)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  item.current
                    ? 'bg-[#2AB7A6] text-black shadow-lg'
                    : 'theme-text-quaternary hover:theme-text-secondary hover:theme-bg-quaternary'
                }`}
              >
                <Icon className={`h-4 w-4 ${item.current ? 'text-black' : 'theme-text-quaternary'}`} />
                <span>{item.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="space-y-6 p-6">
          {/* Enhanced Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold theme-text-primary flex items-center">
                <TrendingUp className="h-6 w-6 mr-3 text-[#2AB7A6]" />
                Portfolio
              </h1>
              <p className="theme-text-quaternary mt-2">
                Monitor your positions, track performance, and manage your trading portfolio
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button className="btn-secondary flex items-center justify-center">
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </button>
              <button className="btn-primary flex items-center justify-center">
                <Plus className="h-4 w-4 mr-2" />
                Open Position
              </button>
            </div>
          </div>

          {/* Enhanced Portfolio Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card p-4 border theme-border hover:border-[#2AB7A6] transition-colors duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium theme-text-quaternary">Balance</p>
                  <p className="text-2xl font-bold theme-text-primary">
                    ${portfolioStats.balance.toLocaleString()}
                  </p>
                </div>
                <div className="h-12 w-12 bg-[#2AB7A6]/10 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-[#2AB7A6]" />
                </div>
              </div>
            </div>

            <div className="card p-4 border theme-border hover:border-[#2AB7A6] transition-colors duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium theme-text-quaternary">Equity</p>
                  <p className="text-2xl font-bold theme-text-primary">
                    ${portfolioStats.equity.toLocaleString()}
                  </p>
                  <p className="text-sm text-[#2AB7A6]">+{portfolioStats.totalReturn}%</p>
                </div>
                <div className={`h-12 w-12 ${theme === 'light' ? 'bg-[#2AB7A6]/10' : 'bg-[#2AB7A6]/20'} rounded-lg flex items-center justify-center`}>
                  <TrendingUp className={`h-6 w-6 ${theme === 'light' ? 'text-[#2AB7A6]' : 'text-[#2AB7A6]'}`} />
                </div>
              </div>
            </div>

            <div className="card p-4 border theme-border hover:border-[#2AB7A6] transition-colors duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium theme-text-quaternary">
                    Unrealized P&L
                  </p>
                  <p
                    className={`text-2xl font-bold ${portfolioStats.unrealizedPnL >= 0 ? 'text-[#2AB7A6]' : 'text-red-400'}`}
                  >
                    {portfolioStats.unrealizedPnL >= 0 ? '+' : ''}$
                    {portfolioStats.unrealizedPnL.toLocaleString()}
                  </p>
                  <p className="text-sm theme-text-quaternary">Today: +${portfolioStats.dailyPnL}</p>
                </div>
                <div className={`h-12 w-12 ${theme === 'light' ? 'bg-yellow-100' : 'bg-yellow-900'} rounded-lg flex items-center justify-center`}>
                  <BarChart3 className={`h-6 w-6 ${theme === 'light' ? 'text-yellow-600' : 'text-yellow-400'}`} />
                </div>
              </div>
            </div>

            <div className="card p-4 border theme-border hover:border-[#2AB7A6] transition-colors duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium theme-text-quaternary">Margin Level</p>
                  <p className="text-2xl font-bold theme-text-primary">
                    {portfolioStats.marginLevel}%
                  </p>
                  <p className="text-sm theme-text-quaternary">Free: ${portfolioStats.freeMargin.toLocaleString()}</p>
                </div>
                <div className={`h-12 w-12 ${theme === 'light' ? 'bg-purple-100' : 'bg-purple-900'} rounded-lg flex items-center justify-center`}>
                  <Shield className={`h-6 w-6 ${theme === 'light' ? 'text-purple-600' : 'text-purple-400'}`} />
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Tabs */}
          <div className="card p-4">
            <div className="border-b theme-border">
              <nav className="-mb-px flex space-x-8">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        activeTab === tab.id
                          ? 'border-[#2AB7A6] text-[#2AB7A6]'
                          : 'border-transparent theme-text-quaternary hover:theme-text-secondary hover:theme-border'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.name}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Enhanced Tab Content */}
            <div className="mt-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Financial Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="theme-bg-quaternary rounded-lg p-4 border theme-border">
                      <h3 className="text-sm font-semibold theme-text-primary mb-4 flex items-center">
                        <Activity className="h-4 w-4 mr-2 text-[#2AB7A6]" />
                        Financial Summary
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="theme-text-quaternary">Total Equity:</span>
                          <span className="theme-text-primary font-medium">
                            ${portfolioStats.equity.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="theme-text-quaternary">Used Margin:</span>
                          <span className="theme-text-primary font-medium">
                            ${portfolioStats.margin.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="theme-text-quaternary">Free Margin:</span>
                          <span className="theme-text-primary font-medium">
                            ${portfolioStats.freeMargin.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="theme-text-quaternary">Realized P&L:</span>
                          <span className="theme-text-primary font-medium">
                            ${portfolioStats.realizedPnL.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="theme-bg-quaternary rounded-lg p-4 border theme-border">
                      <h3 className="text-sm font-semibold theme-text-primary mb-4 flex items-center">
                        <Target className="h-4 w-4 mr-2 text-[#2AB7A6]" />
                        Risk Metrics
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="theme-text-quaternary">Margin Level:</span>
                          <span className="theme-text-primary font-medium">
                            {portfolioStats.marginLevel}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="theme-text-quaternary">Open Positions:</span>
                          <span className="theme-text-primary font-medium">
                            {openPositions.length}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="theme-text-quaternary">Pending Orders:</span>
                          <span className="theme-text-primary font-medium">
                            {pendingOrders.length}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="theme-text-quaternary">Weekly P&L:</span>
                          <span className="theme-text-primary font-medium">
                            ${portfolioStats.weeklyPnL.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'positions' && (
                <div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y theme-border">
                      <thead className="table-header">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Symbol
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Size
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Open Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Current Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            P&L
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Swap
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y theme-border">
                        {openPositions.map(position => (
                          <tr key={position.id} className="table-row hover:theme-bg-quaternary transition-colors duration-200">
                            <td className="px-6 py-4 whitespace-nowrap table-cell font-medium">
                              {position.symbol}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap table-cell">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  position.type === 'buy'
                                    ? theme === 'light' ? 'bg-[#2AB7A6]/10 text-[#2AB7A6]' : 'bg-[#2AB7A6]/20 text-[#2AB7A6]'
                                    : theme === 'light' ? 'bg-red-100 text-red-800' : 'bg-red-900 text-red-300'
                                }`}
                              >
                                {position.type === 'buy' ? <Plus className="h-3 w-3 mr-1" /> : <Minus className="h-3 w-3 mr-1" />}
                                {position.type.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap table-cell">
                              {position.size}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap table-cell">
                              ${position.openPrice.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap table-cell">
                              ${position.currentPrice.toLocaleString()}
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap table-cell font-medium ${
                                position.unrealizedPnL >= 0
                                  ? 'text-[#2AB7A6]'
                                  : 'text-red-400'
                              }`}
                            >
                              {position.unrealizedPnL >= 0 ? '+' : ''}$
                              {position.unrealizedPnL.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap table-cell text-red-400">
                              ${position.swap.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap table-cell">
                              <button
                                onClick={() => closePosition(position.id)}
                                className="text-red-400 hover:text-red-300 transition-colors duration-200 p-2 rounded hover:bg-red-400/10 flex items-center justify-center"
                                title="Close Position"
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'orders' && (
                <div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y theme-border">
                      <thead className="table-header">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Symbol
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Order Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Size
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y theme-border">
                        {pendingOrders.map(order => (
                          <div
                            key={order.id}
                            className="grid grid-cols-7 gap-2 text-xs py-2 px-2 border-b theme-border hover:theme-bg-quaternary/50 transition-colors duration-200 items-center"
                          >
                           <div className="font-medium theme-text-primary">
                             {order.symbol}
                           </div>
                           <div className="flex items-center">
                             <span
                               className={
                                 order.type === 'buy'
                                   ? 'text-[#2AB7A6]'
                                   : 'text-red-400'
                               }
                             >
                               {order.type}
                             </span>
                           </div>
                           <div className="theme-text-secondary">{order.size}</div>
                           <div className="theme-text-secondary">{order.price}</div>
                           <div className="flex items-center">
                             <div className="p-1 rounded bg-yellow-500/20 mr-2">
                               <span className="text-yellow-400 text-xs">⏰</span>
                             </div>
                             <span className="text-yellow-400">{order.status}</span>
                           </div>
                         </div>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'history' && (
                <div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y theme-border">
                      <thead className="table-header">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Symbol
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Size
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Open Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Close Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            P&L
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                            Duration
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y theme-border">
                        {closedPositions.map(position => (
                          <div
                            key={position.id}
                            className="grid grid-cols-7 gap-2 text-xs py-2 px-2 border-b theme-border hover:theme-bg-quaternary/50 transition-colors duration-200 items-center"
                          >
                           <div className="font-medium theme-text-primary">
                             {position.symbol}
                           </div>
                           <div className="flex items-center">
                             <div
                               className={`p-1 rounded mr-2 ${position.type === 'Buy' ? 'bg-[#2AB7A6]/20' : 'bg-red-500/20'}`}
                             >
                               <span className={`text-xs ${position.type === 'Buy' ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                                 {position.type === 'Buy' ? '↗' : '↘'}
                               </span>
                             </div>
                             <span
                               className={
                                 position.type === 'Buy'
                                   ? 'text-[#2AB7A6]'
                                   : 'text-red-400'
                               }
                             >
                               {position.type}
                             </span>
                           </div>
                           <div className="theme-text-secondary">{position.size}</div>
                           <div className="theme-text-secondary">${position.openPrice.toLocaleString()}</div>
                           <div className="theme-text-secondary">${position.closePrice.toLocaleString()}</div>
                           <div className="text-right">
                             <div className={`font-medium ${position.realizedPnL >= 0 ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                               {position.realizedPnL >= 0 ? '+' : ''}${position.realizedPnL.toFixed(2)}
                             </div>
                             <div className="text-xs theme-text-quaternary">
                               {position.duration}
                             </div>
                           </div>
                           <div className="theme-text-quaternary text-xs">
                             {position.closeTime}
                           </div>
                         </div>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Add padding at the end */}
        <div className="pb-[50px]"></div>
      </div>
    </div>
  );
};

export default Portfolio;
